import React, { useState, useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './VideoPlayerStyles.css';
import VideoControls from './VideoControls';
import VideoStatus from './VideoStatus';
import VideoProgressBar from "./VideoProgressBar";

const VideoPlayer = ({ videoData, initialMode, onApproveVideo, onRejectVideo, isActionSubmitting }) => {
  const videoContainerRef = useRef(null);
  const playerRef = useRef(null);
  const videoMetaRef = useRef(videoData?.vidMeta || {});

  const fps = 0;
  const totalFrames = videoData?.vidMeta?.FRAMES?.length || 0;
  const fpsRef = useRef(fps);
  const totalFramesRef = useRef(totalFrames);
  const currentModeRef = useRef(initialMode || 'P');

  const [totalTime, setTotalTime] = useState(0);
  const [totalTransitTime, setTotalTransitTime] = useState('00:00:00');
  const [frameDuration, setFrameDuration] = useState(0);
  const [currentTransitTime, setCurrentTransitTime] = useState('00:00:00');
  const [playbackRates] = useState([5, 4, 3, 2.5, 2, 1.5, 1, 0.5]);
  const [currentFrame, setCurrentFrame] = useState(1);
  const lastViewFrame = useRef(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    if (!videoData) return;

    // Update the metadata ref immediately
    videoMetaRef.current = videoData.vidMeta || {};

    // --- INITIALIZATION ---
    // If the player instance does not exist, create it.
    if (!playerRef.current) {
      const container = videoContainerRef.current;
      if (!container) return;

      // 1. Programmatically create the video element
      // This prevents React from losing the ref when Video.js disposes the player
      const videoElement = document.createElement("video-js");
      videoElement.classList.add('vjs-fluid');
      
      // 2. Create the Fallback Message Element
      const fallbackMsg = document.createElement("p");
      fallbackMsg.className = "vjs-no-js";
      fallbackMsg.innerHTML = `
        To view this video please enable JavaScript, and consider upgrading to a web browser that
        <a href="https://videojs.com/html5-video-support/" target="_blank">supports HTML5 video</a>
      `;

      // 3. Append fallback to video, and video to container
      videoElement.appendChild(fallbackMsg);
      container.appendChild(videoElement);

      // 4. Initialize Player
      const player = videojs(videoElement, {
        controls: false,
        autoplay: false,
        preload: 'auto',
        sources: [{
          src: videoData.panoVidSrc,
          type: 'video/mp4'
        }],
        controlBar: false
      });

      playerRef.current = player;

      // --- EVENTS ---
      player.on('loadedmetadata', () => {
        const meta = videoMetaRef.current;
        const totalFrames = meta?.FRAMES?.length || 0;
        totalFramesRef.current = totalFrames;

        const calculatedTotalTime = player.duration();
        // Avoid division by zero
        const calculatedFps = (totalFrames > 0 && calculatedTotalTime > 0)
          ? Math.round(totalFrames / calculatedTotalTime)
          : 30;

        const calculatedFrameDuration = parseFloat(currentModeRef.current === 'D' ? 2 / calculatedFps : 1 / calculatedFps).toFixed(6);

        setTotalTime(calculatedTotalTime);
        if (meta.FRAMES && meta.FRAMES.length > 0) {
          setTotalTransitTime(meta.FRAMES[totalFrames - 1]);
        }

        fpsRef.current = calculatedFps;
        setFrameDuration(calculatedFrameDuration);

        // console.log('Total time:', calculatedTotalTime);
        // console.log('FPS:', calculatedFps);
        // console.log('Frame duration:', calculatedFrameDuration);

        const newFrame = convertFrameNumber(lastViewFrame.current);
        goToFrame(newFrame || 1);
      });

      player.on('play', () => setIsPlaying(true));
      player.on('pause', () => setIsPlaying(false));
      player.on('seeking', () => setIsBuffering(true));
      player.on('seeked', () => {
        setIsBuffering(false);
        const curTime = player.currentTime();
        if (curTime === 0) {
          player.currentTime(0.01);
        }
        if (curTime * 1000000 % 100 > 95) {
          player.currentTime(curTime - 0.00005);
        }
      });

      player.on('timeupdate', () => {
        const newFrame = convertFrameNumber(timeToFrameNumber(player.currentTime()));
        setCurrentFrame(newFrame);
        if (videoMetaRef.current.FRAMES && videoMetaRef.current.FRAMES[newFrame - 1]) {
          setCurrentTransitTime(videoMetaRef.current.FRAMES[newFrame - 1]);
        }
      });
    } else {
      // If player already exists and videoData changes, just update the source
      const player = playerRef.current;
      if (player.src() !== videoData.panoVidSrc) {
        player.src({ type: 'video/mp4', src: videoData.panoVidSrc });
      }
    }

  }, [videoData]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  const timeToFrameNumber = (time) => {
    const frameNumber = Math.min(Math.ceil(time * fpsRef.current), totalFramesRef.current);
    return frameNumber;
  };

  const convertFrameNumber = (frameNo) => {
    if (currentModeRef.current === 'D') {
      return Math.floor((frameNo - 1) / 2) * 2 + 1;
    }
    return frameNo;
  };

  const goToFrame = (frameNo) => {
    if (isNaN(frameNo) || frameNo > totalFramesRef.current || frameNo < 1) {
      return;
    }
    if (!playerRef.current) return;
    
    const newTime = parseFloat(convertFrameNumber(frameNo) / fpsRef.current) - 0.01;
    playerRef.current.pause();
    playerRef.current.currentTime(newTime);
  };

  const nextFrame = () => {
    if (!playerRef.current) return;
    const player = playerRef.current;
    player.pause();
    const newTime = Math.min(parseFloat(player.currentTime()) + parseFloat(frameDuration), player.duration());
    player.currentTime(newTime);
  };

  const prevFrame = () => {
    if (!playerRef.current) return;
    const player = playerRef.current;
    player.pause();
    const newTime = Math.max(parseFloat(player.currentTime()) - parseFloat(frameDuration), 0.01);
    player.currentTime(newTime);
  };

  const playPauseVideo = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pause();
    } else {
      playerRef.current.play();
    }
  };

  const handlePlaybackRateChange = (rate) => {
    if (!playerRef.current) return;
    playerRef.current.playbackRate(rate);
    setPlaybackRate(rate);
  };

  const handleSeek = (frame) => {
    if (!playerRef.current) return;
    const time = (frame / fpsRef.current) - 0.01;
    playerRef.current.currentTime(time);
  };

  const switchViewMode = () => {
    if (!playerRef.current) return;
    lastViewFrame.current = currentFrame;
    const newMode = currentModeRef.current === 'P' ? 'D' : 'P';
    currentModeRef.current = newMode;
    const newSrc = newMode === 'P' ? videoData.panoVidSrc : videoData.duoVidSrc;
    playerRef.current.src({ type: 'video/mp4', src: newSrc });
  };

  return (
    <div className="min-w-[1024px] min-h-[720px] h-screen w-full p-0 text-gray-300 bg-gray-800 grid grid-cols-[9fr,auto] grid-rows-[1fr,auto]">
      <div className="flex flex-col max-h-full justify-center overflow-y-auto">
        <div className="bg-black w-full relative" onClick={playPauseVideo}>
          {/* Container for Video.js */}
          <div ref={videoContainerRef} />
        </div>
      </div>
      <div className="bg-gray-700"></div>
      <div className="col-span-2 bg-gray-700 flex flex-col justify-between min-h-[120px] p-4">
        <VideoStatus
          currentFrame={currentFrame}
          totalFrames={totalFrames}
          currentTime={currentTransitTime}
          totalTime={totalTransitTime}
        />
        <VideoProgressBar
          currentFrame={currentFrame}
          totalFrames={totalFramesRef.current}
          onSeek={handleSeek}
          thumbnails={videoMetaRef.current?.ThumbnailPopup}
          frameTimestamps={videoMetaRef.current?.FRAMES}
        />
        <VideoControls
          onPrevFrame={prevFrame}
          onNextFrame={nextFrame}
          onPlayPause={playPauseVideo}
          isPlaying={isPlaying}
          playbackRates={playbackRates}
          currentPlaybackRate={playbackRate}
          onPlaybackRateChange={handlePlaybackRateChange}
          switchViewMode={switchViewMode}
          approveVideo={onApproveVideo}
          rejectVideo={onRejectVideo}
          isActionSubmitting={isActionSubmitting}
        />
      </div>
    </div>
  );
};

export default VideoPlayer;
